import { useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { ImportHistory } from '@multaqa/ui/components/import/import-history'
import { Button } from '@multaqa/ui/components/ui/button'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Spinner } from '@multaqa/ui/components/ui/spinner'
import { DownloadIcon, UploadIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { isWorking, useDataImports, useImportSections, useUploadImport } from './api'
import { useImportViewText } from './data-import-labels'
import { DataImportPanel } from './data-import-panel'
import { DataImportTemplateDialog } from './data-import-template-dialog'

/**
 * The admin door of the data import for one company
 * (git show 6cb363968d89d3af890b4608cb1bb4193eb06efe:docs/design/cross-cutting/2026-10-07-tenant-data-import.md): download a
 * template, upload the filled workbook, read the preview, then apply it.
 */
export function CompanyDataImport({
  groupId,
  companyId,
  companyName,
}: {
  groupId: string
  companyId: string
  companyName: string
}) {
  const { t } = useTranslation()
  const text = useImportViewText()
  const messages = useAppErrorMessages()
  const sections = useImportSections(groupId, companyId)
  const imports = useDataImports(groupId, companyId)
  const upload = useUploadImport(groupId, companyId)
  const fileInput = useRef<HTMLInputElement>(null)
  // null: nothing chosen yet (the newest import may open itself);
  // '': the admin closed the panel, so nothing opens on its own.
  const [chosen, setChosen] = useState<string | null>(null)
  const [templateOpen, setTemplateOpen] = useState(false)

  if (imports.isLoading || sections.isLoading)
    return <ListSkeleton columns={[1.5, 2, 1.5, 1, 1, 0.5]} rows={4} />
  if (imports.isError)
    return <AppError error={imports.error} messages={messages} retry={imports.refetch} />
  if (sections.isError)
    return <AppError error={sections.error} messages={messages} retry={sections.refetch} />

  const list = imports.data ?? []
  // Open the newest import by default while it still needs attention.
  const latest = list[0]
  const openId =
    chosen ?? (latest && (isWorking(latest.status) || latest.status === 'ready') ? latest.id : null)

  return (
    <section data-testid="company-data-import" aria-labelledby="company-data-import-heading">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h2 id="company-data-import-heading" className="text-h3">
            {t('companies.imports.title')}
          </h2>
          <p className="text-small text-muted-foreground">{t('companies.imports.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setTemplateOpen(true)}
            data-testid="data-import-template"
          >
            <DownloadIcon className="size-3.5" aria-hidden="true" />
            {t('companies.imports.template')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={upload.isPending}
            onClick={() => fileInput.current?.click()}
            data-testid="data-import-upload"
          >
            {upload.isPending ? (
              <Spinner className="size-3.5" />
            ) : (
              <UploadIcon className="size-3.5" aria-hidden="true" />
            )}
            {t('companies.imports.upload')}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            aria-label={t('companies.imports.upload')}
            data-testid="data-import-file"
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (!file) return
              upload.mutate(file, {
                onSuccess: (created) => setChosen(created.id),
                onError: () => toast.error(t('companies.imports.uploadFailed')),
              })
            }}
          />
        </div>
      </div>
      <p className="-mt-2 mb-4 text-small text-muted-foreground">
        {t('companies.imports.uploadHint')}
      </p>

      {openId ? (
        <DataImportPanel
          key={openId}
          groupId={groupId}
          companyId={companyId}
          companyName={companyName}
          importId={openId}
          onClose={() => setChosen('')}
          onAct={() => setChosen(openId)}
        />
      ) : null}

      <ImportHistory
        items={list}
        selectedId={openId}
        onOpen={setChosen}
        labels={text.history}
        formatDate={text.formatDate}
        formatResult={text.formatResult}
        doorLabel={text.doorLabel}
        statusLabel={text.statusLabel}
      />

      {templateOpen ? (
        <DataImportTemplateDialog
          groupId={groupId}
          companyId={companyId}
          sections={sections.data ?? []}
          open
          onOpenChange={setTemplateOpen}
        />
      ) : null}
    </section>
  )
}
